import React from 'react';
import { styled } from '@mui/material/styles';
import { Paper, Typography, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { StageState } from '../types';
import { StageIcon } from './StageIcon';
import { ProgressBar } from './ProgressBar';
import { MessageList } from './MessageList';

const StageContainer = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  overflow: 'hidden',
}));

const StageHeader = styled('div')<{ clickable: boolean }>(({ theme, clickable }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.25, 2, 0.75, 2),
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: clickable ? theme.palette.action.hover : 'transparent',
  },
}));

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginBottom: 4,
});

const IconContainer = styled('div')({
  width: 28,  // Same as icon width
  marginRight: 8,
  display: 'flex',
  alignItems: 'center',
});

const ExpandIconContainer = styled('div')({
  width: 28,  // Consistent with other icons
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StageContent = styled('div')({
  position: 'relative',
  width: 'calc(100% - 36px)', // subtract icon width (20) + margins (8 + 8)
  marginLeft: 28, // icon width (20) + margin (8)
});


const ProgressWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
}));

interface StageNodeProps {
  stage: StageState;
  onToggle: () => void;
}

export const StageNode: React.FC<StageNodeProps> = ({ stage, onToggle }) => {
  const hasMessages = stage.messages.length > 0;
  const showProgress = stage.status === 'in_progress' || stage.progress > 0;

  return (
    <StageContainer elevation={1}>
      <StageHeader
        onClick={hasMessages ? onToggle : undefined}
        clickable={hasMessages}
      >
        <HeaderRow>
          <IconContainer>
            <StageIcon status={stage.status} />
          </IconContainer>
          <Tooltip title={stage.name} enterDelay={500}>
            <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
              {stage.name}
            </Typography>
          </Tooltip>
          <ExpandIconContainer>
            {hasMessages && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              >
                {stage.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </ExpandIconContainer>
        </HeaderRow>

        {showProgress && (
          <StageContent>
            <ProgressWrapper>
              <ProgressBar
                progress={stage.progress}
                status={stage.status}
              />
            </ProgressWrapper>
          </StageContent>
        )}
      </StageHeader>

      <MessageList
        messages={stage.messages}
        isExpanded={stage.isExpanded}
      />
    </StageContainer>
  );
}; 